import { TemplateRef } from '@angular/core';
import { CheckboxFilterComponent } from '@/shared/components/table/components/checkbox-filter/checkbox-filter.component';
import { SearchbarComponent } from './components/searchbar/searchbar.component';
import { Column, ColumnType, ColumnAlignment, TableDataSource, PaginationMetadata, InviteStatus } from './table.model';
import { Component, Input, AfterViewInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { PaginatorComponent, PaginatorType } from '@/shared/components/table/components/paginator/paginator.component';
import { MatSort } from '@angular/material/sort';
import { FilterComponent } from '@/shared/components/table/components/filter/filter.component';
import { v4 as uuidv4 } from 'uuid';
import { skip } from "rxjs/operators";

export enum DataSourceName {
  BlockUsername = "BLOCK USERNAME",
  Clusters = "CLUSTERS",
  Dashboard = "DASHBOARD",
  Elements = "ELEMENTS",
  Explore = "EXPLORE",
  IpBlacklist = "IP_BLACKLIST",
  Onboarding = "ONBOARDING",
  Users = "USERS",
  Waitlist = "WAITLIST",
}

@Component({
  selector: 'cosmos-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent<TData> implements AfterViewInit {
  @Input() public set columns(cols: Column<TData>[]) {
    this._columns = cols;
    this._displayedColumns = cols
      .filter((c) => !c.hidden)
      .map(c => c.columnId);
  }

  @Input() public set dataSource(source: TableDataSource<TData>) {
    this._dataSource = source;
    if (!!this._dataSource) {
      this._dataSource.dataChanged$?.pipe(
        // Datasource emits 2 values on initialization which are skipped to omit invoking scrollToTop function
        skip(2),
      ).subscribe(_ => this.scrollToTop());
    }
  }

  @Input() public dataSourceName: DataSourceName;
  @Input() public hasDropdownFilter = false;
  @Input() public hasSearchbar = false;
  @Input() public hasCheckboxFilter = false;
  @Input() public isFilterable = true;
  @Input() public isPaginable = true;
  @Input() public controlPanelTemplate: TemplateRef<any>;
  @Input() public optionalSummaryTemplate: TemplateRef<any>;
  @Input() public paginatorType: PaginatorType = 'default';
  @Input() public isDownloading = false;
  @Output() public clickedRow = new EventEmitter<TData>();

  @ViewChild(PaginatorComponent) public paginator: PaginatorComponent;
  @ViewChild(MatSort) public sort: MatSort;
  @ViewChild(FilterComponent) public filter: FilterComponent;
  @ViewChild(SearchbarComponent) public searchbar: SearchbarComponent;
  @ViewChild(CheckboxFilterComponent) public checkboxFilter: CheckboxFilterComponent;

  public _dataSource: TableDataSource<TData>;
  public _columns: Column<TData>[] = [];
  public _displayedColumns: string[];
  public _searchbarPlaceholder: string = 'Search for...';
  public _paginationDetails: PaginationMetadata;
  public readonly tableId = uuidv4();
  public readonly ColumnType = ColumnType;
  public readonly ColumnAlignment = ColumnAlignment;
  public readonly InviteStatus = InviteStatus;

  public ngAfterViewInit() {
    this._dataSource.paginator = this.paginator;
    this._dataSource.sort = this.sort;
    if (this.hasDropdownFilter) {
      this._dataSource.filter = this.filter;
    }
    if (this.hasSearchbar) {
      this._dataSource.searchbar = this.searchbar;
    }
    if (this.hasCheckboxFilter) {
      this._dataSource.checkboxFilter = this.checkboxFilter;
    }

    if (this.dataSourceName === DataSourceName.Elements) {
      this._searchbarPlaceholder = 'Search by Element ID...';
    }
  }

  public onTableRowClick(row: TData) {
    this.clickedRow.emit(row);
  }

  public scrollToTop(): void {
    const table = document.getElementById(this.tableId);
    table!.scrollIntoView({ behavior: "smooth" });
  }
}
